import clsx from 'clsx';
import React, { memo, useEffect, useMemo, useState } from 'react';

import { TRect } from '@/types';

import { RENDER_BBOX } from '../../constants';
import { TSegmentThumbnail } from '../../types';
import { buildThumbStyle, resolveThumbnail } from '../../utils';
import { NoClipThumbnail } from '../NoClipThumbnail';

type SpritesheetThumbnailProps = {
  startOffsetMs: number | null;
  endOffsetMs?: number | null;
  thumbnails: TSegmentThumbnail[];
  bbox: TRect | undefined | null;
  displayWidth?: number;
  alt: string;
  title?: string;
  className?: string;
  onClick?: () => void;
  presenceImageUrl?: string;
};

export const SpritesheetThumbnail = memo(function SpritesheetThumbnail({
  startOffsetMs,
  endOffsetMs,
  thumbnails,
  bbox,
  displayWidth,
  alt,
  title,
  className,
  onClick,
  presenceImageUrl,
}: SpritesheetThumbnailProps) {
  const thumb = useMemo(
    () => resolveThumbnail(startOffsetMs, thumbnails),
    [startOffsetMs, endOffsetMs, thumbnails]
  );

  const styles = useMemo(() => {
    if (!thumb) return null;
    return buildThumbStyle(
      thumb.url,
      thumb.x,
      thumb.y,
      thumb.width,
      thumb.height,
      bbox,
      displayWidth
    );
  }, [thumb, bbox, displayWidth]);

  const [imageLoaded, setImageLoaded] = useState(false);
  useEffect(() => {
    if (!thumb) return;
    setImageLoaded(false);
    const img = new window.Image();
    img.onload = () => setImageLoaded(true);
    img.src = thumb.url;
    if (img.complete) setImageLoaded(true);
    return () => {
      img.onload = null;
    };
  }, [thumb?.url]);

  const commonProps = {
    role: 'img' as const,
    'aria-label': alt,
    title: title ?? alt,
    className: clsx('rounded bg-black', className),
    onClick,
  };

  if (!styles) {
    const cellW = thumbnails[0]?.width ?? 320;
    const cellH = thumbnails[0]?.height ?? 160;
    const scale = displayWidth != null ? displayWidth / cellW : 1;

    return (
      <NoClipThumbnail
        className={clsx('rounded', className)}
        title={title ?? alt}
        style={{ width: cellW * scale, height: cellH * scale }}
        onClick={onClick}
        presenceImageUrl={presenceImageUrl}
      />
    );
  }

  let bboxOverlay: React.ReactElement | null = null;
  if (RENDER_BBOX && imageLoaded && styles.bboxRect) {
    const { left, top, width, height } = styles.bboxRect;
    bboxOverlay = (
      <div
        style={{
          position: 'absolute',
          left,
          top,
          width,
          height,
          border: '2px solid #ef4444',
          boxSizing: 'border-box',
          pointerEvents: 'none',
        }}
      />
    );
  }

  return (
    <div {...commonProps} style={styles.containerStyle}>
      <div style={styles.innerStyle} />
      {bboxOverlay}
    </div>
  );
});
